// The Voulgaris-Style Edge §4 (Edge 3) — Volume + ROI over win rate.
//
// "ROI x Volume x Time = wealth. Wins x Losses = noise." Pure, no
// I/O. Computes the account ROI summary from the user's REAL settled
// slips — never a fabricated P&L.
//
// Doc §4.1 + §13 prohibitions, enforced here:
//   • ROI is the primary metric, not win rate.
//   • Show ROI only when settled sample ≥ 20 slips. Below that,
//     sampleSufficient=false and the UI shows a sample-size warning
//     instead of a misleading ROI.
//   • Only definite-outcome slips with a real stake contribute to
//     P&L. 'partial' (leg-level unknown without detail) and slips
//     missing stake/odds are excluded from P&L — never guessed.
//     'void' = stake returned (0 P&L) but counts as a settled slip.

/** Minimal slip shape this module needs (subset of UserSlip). */
export interface RoiSlip {
  readonly status: "building" | "locked" | "settled";
  readonly outcome: "won" | "lost" | "partial" | "void" | null;
  readonly combined_odds: number | null;
  readonly stake_amount: number | null;
}

/** Settled-slip floor below which ROI is not shown (doc §13). */
export const ROI_MIN_SLIPS = 20;

export interface RoiSummary {
  /** Settled slips with a definite outcome + real stake (the sample). */
  readonly settledSlips: number;
  readonly winningSlips: number;
  readonly winPct: number | null;
  readonly totalStaked: number;
  readonly netPnl: number;
  readonly roiPct: number | null;
  /** True once settledSlips ≥ ROI_MIN_SLIPS — gate ROI display. */
  readonly sampleSufficient: boolean;
}

function num(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function computeRoiSummary(
  slips: readonly RoiSlip[],
): RoiSummary {
  let settled = 0;
  let winning = 0;
  let staked = 0;
  let pnl = 0;
  for (const s of slips) {
    if (s.status !== "settled") continue;
    const stake = num(s.stake_amount);
    // Need a real positive stake + a definite outcome to score P&L.
    if (stake === null || stake <= 0) continue;
    if (s.outcome === "won") {
      const odds = num(s.combined_odds);
      if (odds === null || odds <= 1) continue; // can't score honestly
      settled += 1;
      winning += 1;
      staked += stake;
      pnl += stake * (odds - 1);
    } else if (s.outcome === "lost") {
      settled += 1;
      staked += stake;
      pnl -= stake;
    } else if (s.outcome === "void") {
      // Stake returned — counts as a settled slip, zero P&L, and the
      // stake is NOT added to the staked base (no risk was taken).
      settled += 1;
    }
    // 'partial' / null ⇒ excluded entirely (honest — not guessed).
  }
  const sampleSufficient = settled >= ROI_MIN_SLIPS;
  return {
    settledSlips: settled,
    winningSlips: winning,
    winPct: settled > 0 ? +((winning / settled) * 100).toFixed(1) : null,
    totalStaked: +staked.toFixed(2),
    netPnl: +pnl.toFixed(2),
    roiPct: staked > 0 ? +((pnl / staked) * 100).toFixed(1) : null,
    sampleSufficient,
  };
}

/** Voulgaris context line (doc §4.1) — shown below the ROI figure. */
export const VOULGARIS_ROI_CONTEXT =
  "Voulgaris averaged 6% ROI across 1,000+ bets per season. ROI × Volume × Time = wealth. Wins × Losses = noise.";

/** Sample-size warning copy (doc §4.1) when settled < 20. */
export function roiSampleWarning(settled: number): string {
  return `Your ROI over ${settled} settled slip${
    settled === 1 ? "" : "s"
  } is not statistically meaningful yet. Continue building your sample to at least ${ROI_MIN_SLIPS}.`;
}
